<template>
  <div class="pdp-product-details">
    <section id="buying-zone" class="buying-zone grid-container mb-6" data-pdp>
      <div class="grid-x grid-margin-x">
        <div class="buying-zone__carousel small-12 large-8 cell mb-2" :data-brand-name="product.brandNameFull">
          <input id="product-name" type="hidden" :value="product.name" />
          <input id="product-code" type="hidden" :value="variant.code" />
          <input id="product-brand" type="hidden" :value="variant.brandNameFull" />
          <!-- b2ct Image Panel -->
          <pdp-product-image-panel
            v-if="hasImages"
            :product="product"
            :color="selectedColor"
            :base-properties="baseProperties"
            :max-thumbnails="options.maxPdpThumbnails"
          />
        </div>
        <div class="small-12 large-4 cell">
          <div class="grid-x grid-margin-x">
            <pdp-product-header
              :product="product"
              :options="options"
              :variant-code="variantCode"
              :base-properties="baseProperties"
            />
            <footer class="buying-zone__footer small-12 medium-6 large-12 cell" data-buying-zone>
              <div class="mb-2">
                <div class="flex flex-col mb-2">
                  <div class="buying-zone__price-wrapper">
                    <pdp-product-price
                      v-if="!product.isArchived && !options.hidePrices"
                      :is-variant-selected="!!variantCode"
                      :product="product"
                      :consumer="consumerPrice"
                      :base-properties="baseProperties"
                    />
                    <span
                      qaid="product-sku"
                      class="buying-zone__code text-xsmall-xstrong"
                      v-html="bp('productDetailsSku', variantCode || product.code)"
                    />
                  </div>
                  <div
                    v-if="hasRetailerPricesMayVaryBP"
                    qaid="retailer-prices-vary-bp"
                    class="text-xs italic"
                    v-html="bp('retailerPricesMayVary')"
                  />
                </div>
                <div
                  v-if="copyPositioningStatement.length > 0"
                  class="buying-zone__positioning-statement mb-2 pt-2 border-t border-gray-10"
                >
                  <p qaid="product-positioning-statement" v-html="copyPositioningStatement" />
                </div>
                <pdp-product-summary :product="product" />
                <product-compare-button
                  v-if="options.productCompareEnabledOnSite"
                  small
                  qaid="compareProductButton"
                  :product="product"
                  :base-properties="baseProperties"
                />
              </div>
              <pdp-product-attributes-container
                ref="attributesContainer"
                v-model="variantCode"
                :product="product"
                :render-stock-status="!options.kioskModeEnabledOnSite && options.eCommAvailable"
                :stock-message="stockMessage"
                :is-loading-stock-data="isLoadingStockData"
                :init-attribute-selection="initAttributeSelection"
                :base-properties="baseProperties"
                @color-changed="
                  (color) => {
                    selectedColor = color;
                  }
                "
              />

              <pdp-build-a-bundle-cta
                v-if="showElectraAccessoryFinderCTA"
                class="cursor-pointer"
                :base-properties="baseProperties"
                :product="product"
                :variant-code="variantCode"
              />

              <pdp-add-to-cart
                :product="product"
                :is-purchasable="isPurchasable"
                :variant-code="variantCode"
                :options="options"
                :message="cartSuccessMessage"
                :add-to-cart-button-caption="addToCartButtonCaption"
                :base-properties="baseProperties"
                :stock-data="stockData"
                :stock-message="stockMessage"
                @add-to-cart="
                  (quantity) => {
                    addToCart(variantCode, quantity);
                  }
                "
              />

              <pdp-customize-product-button
                v-if="!product.isArchived && !options.kioskModeEnabledOnSite"
                :base-properties="baseProperties"
                :project-one-url="projectOneUrl"
                :is-project-one-configurator-active="options.isProjectOneConfiguratorActive"
                :custom-configurator-type="product.customConfiguratorType"
                :custom-product-starting-point="product.customProductStartingPoint"
                :site-locale="options.siteLocale"
                :options="options"
              />

              <div v-if="product.buyingZoneCMSLink && product.buyingZoneCMSLink.length > 0">
                <pdp-buying-zone-cms-link
                  v-for="(cmsLink, i) in product.buyingZoneCMSLink"
                  :key="`cms-link-${i}`"
                  :options="cmsLink"
                />
              </div>
              <pdp-rpa-results
                v-if="!options.kioskModeEnabledOnSite && options.rpaFunctionalityActive && !showLocallyWidget"
                :is-bike="isBike"
                :base-properties="baseProperties"
                :stock-data="stockData"
                :is-location-postal-code-correct="isLocationPostalCodeCorrect"
                :options="options"
                :product="product"
                @updateRpaStockStatus="updateRpaResults"
              />
              <pdp-locally-product-finder
                v-if="!options.kioskModeEnabledOnSite && options.isLocallyActive"
                :variant="variant"
                :base-properties="baseProperties"
                :product="product"
                :options="options"
                :show-locally-widget="showLocallyWidget"
                @change-locally-widget="updateshowLocallyWidget"
              />
            </footer>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {ProductDetails} from '@/components/containers/pdp/utils/product-details';
import {AddToCart} from '@/components/containers/pdp/utils/add-to-cart';
import {InitProductAttributes} from '@/components/containers/pdp/utils/init-product-attributes';
import {ProductStockData} from '@/components/containers/pdp/utils/product-stock-data';
import PdpProductHeader from '@/components/containers/pdp/b2c/PdpProductHeader';
import PdpProductPrice from '@/components/containers/pdp/PdpProductPrice';
import PdpProductSummary from '@/components/containers/pdp/PdpProductSummary';
import ProductCompareButton from '@/components/containers/plp/ProductCard/ProductCompareButton';
import PdpCustomizeProductButton from '@/components/containers/pdp/PdpCustomizeProductButton';
import PdpAddToCart from '@/components/containers/pdp/b2c/PdpAddToCart';
import PdpBuyingZoneCmsLink from '@/components/containers/pdp/PdpBuyingZoneCmsLink';
import PdpRpaResults from '@/components/containers/pdp/PdpRpaResults';
import PdpProductImagePanel from '@/components/containers/pdp/PdpProductImagePanel';
import PdpLocallyProductFinder from '@/components/containers/pdp/PdpLocallyProductFinder';
import PdpProductAttributesContainer from '@/components/containers/pdp/PdpProductAttributesContainer';
import PdpBuildABundleCta from '@/components/containers/pdp/PdpBuildABundleCta';
import {mapState} from 'vuex';

export default {
  name: 'PdpProductDetails',
  components: {
    PdpRpaResults,
    PdpBuyingZoneCmsLink,
    PdpAddToCart,
    PdpCustomizeProductButton,
    ProductCompareButton,
    PdpProductSummary,
    PdpProductPrice,
    PdpProductHeader,
    PdpProductImagePanel,
    PdpProductAttributesContainer,
    PdpLocallyProductFinder,
    PdpBuildABundleCta,
  },
  mixins: [ProductDetails, InitProductAttributes, ProductStockData, AddToCart],
  computed: {
    ...mapState('pdp', ['electraAccessoryFinderToggle', 'pdpFunctionsMapping', 'isElectraBrand']),
    showElectraAccessoryFinderCTA() {
      return (
        this.variantCode &&
        this.isBike &&
        this.electraAccessoryFinderToggle &&
        this.pdpFunctionsMapping.ElectraAccessoryFinder &&
        this.isElectraBrand
      );
    },
    hasRetailerPricesMayVaryBP() {
      return this.baseProperties.some(
        (BP) => BP.name === 'retailerPricesMayVary' && BP.label !== 'text.buyingZone.retailerPricesVary'
      );
    },
  },
  watch: {
    variantCode(code) {
      this.loadPricing(code);
    },
  },
  methods: {
    updateRpaResults(code, product) {
      this.$emit('updateRpaResults', code, product);
    },
  },
};
</script>
